import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { ReactNode, useCallback, useEffect, useRef } from "react";

export interface KanbanItem {
  id: string;
  status: string;
}

interface KanbanCardProps<T extends KanbanItem> {
  item: T;
  onClick: () => void;
  renderContent: (item: T) => ReactNode;
  className?: string;
  settleFrom?: "left" | "right";
}

/**
 * A draggable Kanban card. After a refused move it settles back into place
 * from the side it was dropped on and takes keyboard focus.
 * @param item - The item the card represents
 * @param onClick - Callback when the card is clicked
 * @param renderContent - Render function for the card content
 * @param className - Optional additional CSS classes
 * @param settleFrom - The side the card slides back from after a refused move
 * @returns The draggable card
 */
export function KanbanCard<T extends KanbanItem>({
  item,
  onClick,
  renderContent,
  className,
  settleFrom,
}: KanbanCardProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: "item",
      item,
    },
  });

  const cardRef = useRef<HTMLDivElement | null>(null);
  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      cardRef.current = node;
    },
    [setNodeRef],
  );

  useEffect(() => {
    if (settleFrom) {
      cardRef.current?.focus();
    }
  }, [settleFrom]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setCardRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "bg-card border rounded-lg p-2",
        "cursor-grab active:cursor-grabbing active:scale-[0.98]",
        "transition-[transform,box-shadow,opacity] duration-150 ease-out motion-reduce:!transition-none",
        "hover:-translate-y-px hover:shadow-sm",
        isDragging && "opacity-40 shadow-none scale-[0.98]",
        settleFrom &&
          "motion-safe:animate-in motion-safe:duration-[320ms] motion-safe:ease-[cubic-bezier(0.25,1,0.5,1)]",
        settleFrom === "right" && "motion-safe:slide-in-from-right-6",
        settleFrom === "left" && "motion-safe:slide-in-from-left-6",
        className,
      )}
      onClick={onClick}
    >
      {renderContent(item)}
    </div>
  );
}
